import type { ReactNode, FormEvent } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, formatVND, formatCompact } from '@/lib/utils';
import { parseMoneyOrNull } from '../features/entry/formSchema';

/** Khung chung cho mọi ngăn nhập liệu: tiêu đề, lỗi từ máy chủ, nút lưu. */
export function FormSheet(p: {
  open: boolean; onClose: () => void; title: string; description?: string;
  onSubmit: () => void; submitLabel: string; busy?: boolean; error?: string | null; disabled?: boolean; children: ReactNode;
}) {
  const isDesktop = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches;
  const submit = (e: FormEvent) => { e.preventDefault(); p.onSubmit(); };
  return (
    <Sheet open={p.open} onOpenChange={(o) => !o && p.onClose()}>
      <SheetContent side={isDesktop ? 'right' : 'bottom'} className="max-h-[92dvh] overflow-y-auto overscroll-contain pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{p.title}</SheetTitle>
          {p.description && <SheetDescription>{p.description}</SheetDescription>}
        </SheetHeader>
        <form onSubmit={submit} className="mt-4 space-y-4" noValidate>
          {p.children}
          {p.error && <p role="alert" className="rounded-md bg-bad-soft px-3 py-2 text-sm text-bad">{p.error}</p>}
          <Button type="submit" className="w-full" disabled={p.busy || p.disabled}>{p.busy ? 'Đang lưu…' : p.submitLabel}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {(error || hint) && <p className={cn('num text-xs', error ? 'text-bad' : 'text-muted-foreground')}>{error ?? hint}</p>}
    </div>
  );
}

/** Ô tiền gõ tắt (50k, 1.5tr) kèm xem trước "= 1.500.000 đ". */
export function MoneyField({ label, value, onChange, hint, error, allowZero, id }: {
  label: string; value: string; onChange: (v: string) => void; hint?: ReactNode; error?: string | null; allowZero?: boolean; id?: string;
}) {
  const n = parseMoneyOrNull(value);
  const preview = n !== null && (n > 0 || allowZero) ? `= ${formatVND(n)}` : null;
  return (
    <Field label={label} error={error} hint={preview ?? hint ?? 'Gõ tắt: 50k · 1.5tr · 2 tỷ'}>
      <Input id={id} value={value} onChange={e => onChange(e.target.value)} inputMode="text" autoComplete="off" placeholder="vd: 1.5tr" className="num" aria-invalid={!!error} />
    </Field>
  );
}

export interface WalletLike { id: string; name: string; type?: string; balance?: number; usedCredit?: number; creditLimit?: number }

export function WalletSelect({ label, value, onChange, wallets, placeholder = 'Chọn ví', error }: {
  label: string; value: string; onChange: (id: string) => void; wallets: WalletLike[]; placeholder?: string; error?: string | null;
}) {
  return (
    <Field label={label} error={error}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-invalid={!!error}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {wallets.map(w => (
            <SelectItem key={w.id} value={w.id}>
              {w.name} · {w.type === 'credit' ? `dư nợ ${formatCompact(Number(w.usedCredit) || 0)}${Number(w.creditLimit) > 0 ? `, còn ${formatCompact(Math.max(0, Number(w.creditLimit) - (Number(w.usedCredit) || 0)))}` : ''}` : formatCompact(Number(w.balance) || 0)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
